import {
    AttachmentCategory,
    AttachmentEntityType,
    FinanceSummary,
    PaymentMethod,
    PaymentStatus,
    PaymentType,
    PurchaseProcessStatus,
    PurchaseStatus,
    SupplierQuoteStatus,
} from '@Purchasing/Domain/Model/PurchaseProcess'
import { format } from 'date-fns'

/**
 * Camada de apresentação: enums técnicos NUNCA aparecem para o usuário.
 * Backend/API/database continuam com valores estáveis — aqui só traduzimos.
 * Regra (§17/§30): fallback SEMPRE humano, nunca o valor cru.
 */

// ---------------------------------------------------------------------------
// Datas pt-BR
// ---------------------------------------------------------------------------

export const formatDateTimePt = (d: string | Date | null | undefined) =>
    d ? format(new Date(d), "dd/MM/yyyy 'às' HH:mm") : '—'

export const formatDatePt = (d: string | Date | null | undefined) =>
    d ? format(new Date(d), 'dd/MM/yyyy') : '—'

// ---------------------------------------------------------------------------
// PurchaseProcess
// ---------------------------------------------------------------------------

export const processStatusLabel: Record<PurchaseProcessStatus, string> = {
    DRAFT: 'Rascunho',
    ACTIVE: 'Ativo',
    LOST: 'Perdido',
    CANCELED: 'Cancelado',
    FINISHED: 'Finalizado',
}

export const processStatusLabelSafe = (s: string) =>
    processStatusLabel[s as PurchaseProcessStatus] ?? 'Status do processo'

// ---------------------------------------------------------------------------
// SupplierQuote
// ---------------------------------------------------------------------------

export const quoteStatusLabel: Record<SupplierQuoteStatus, string> = {
    REQUESTED: 'Solicitada',
    RECEIVED: 'Recebida',
    REJECTED: 'Rejeitada',
    CANCELED: 'Cancelada',
}

export const quoteStatusLabelSafe = (s: string) =>
    quoteStatusLabel[s as SupplierQuoteStatus] ?? 'Status da cotação'

// ---------------------------------------------------------------------------
// Purchase
// ---------------------------------------------------------------------------

export const purchaseStatusLabel: Record<PurchaseStatus, string> = {
    DRAFT: 'Rascunho',
    ORDERED: 'Pedido realizado',
    PARTIALLY_RECEIVED: 'Recebido parcialmente',
    RECEIVED: 'Recebido',
    CHECKED: 'Conferido',
    CANCELED: 'Cancelado',
}

export const purchaseStatusLabelSafe = (s: string) =>
    purchaseStatusLabel[s as PurchaseStatus] ?? 'Status da compra'

// ---------------------------------------------------------------------------
// Payment
// ---------------------------------------------------------------------------

export const paymentStatusLabel: Record<PaymentStatus, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    CANCELED: 'Cancelado',
}

export const paymentStatusLabelSafe = (s: string) =>
    paymentStatusLabel[s as PaymentStatus] ?? 'Status do pagamento'

export const paymentTypeLabel: Record<PaymentType, string> = {
    ADVANCE: 'Adiantamento',
    INSTALLMENT: 'Parcela',
    FINAL: 'Pagamento final',
    OTHER: 'Outro',
}

export const paymentTypeLabelSafe = (s: string) =>
    paymentTypeLabel[s as PaymentType] ?? 'Pagamento'

export const paymentMethodLabel: Record<PaymentMethod, string> = {
    PIX: 'Pix',
    BOLETO: 'Boleto',
    BANK_TRANSFER: 'Transferência bancária',
    CARD: 'Cartão',
    CASH: 'Dinheiro',
    OTHER: 'Outro',
}

export const paymentMethodLabelSafe = (s: string) =>
    paymentMethodLabel[s as PaymentMethod] ?? 'Outro método'

export const financeStatusLabel: Record<FinanceSummary['financeStatus'], string> = {
    NOT_PLANNED: 'Sem pagamentos planejados',
    UNPAID: 'A pagar',
    PARTIALLY_PAID: 'Parcialmente pago',
    PAID: 'Pago integralmente',
}

export const financeStatusLabelSafe = (s: string) =>
    financeStatusLabel[s as FinanceSummary['financeStatus']] ?? 'Situação financeira'

// ---------------------------------------------------------------------------
// Attachment
// ---------------------------------------------------------------------------

export const attachmentEntityLabel: Record<AttachmentEntityType, string> = {
    PURCHASE_PROCESS: 'Processo',
    SUPPLIER_QUOTE: 'Cotações',
    PURCHASE: 'Compra',
    PAYMENT: 'Pagamentos',
}

export const attachmentEntityLabelSafe = (s: string) =>
    attachmentEntityLabel[s as AttachmentEntityType] ?? 'Documento'

export const attachmentCategoryLabel: Record<AttachmentCategory, string> = {
    CLIENT_REFERENCE: 'Referência do cliente',
    SUPPLIER_QUOTE: 'Orçamento do fornecedor',
    ARTWORK: 'Arte',
    MOCKUP: 'Mockup',
    PURCHASE_ORDER: 'Pedido de compra',
    INVOICE: 'Nota fiscal',
    BOLETO: 'Boleto',
    PAYMENT_PROOF: 'Comprovante de pagamento',
    OTHER: 'Outro',
}

export const attachmentCategoryLabelSafe = (s: string) =>
    attachmentCategoryLabel[s as AttachmentCategory] ?? 'Documento'

export const attachmentStatusLabel: Record<string, string> = {
    UPLOADING: 'Enviando',
    AVAILABLE: 'Disponível',
    FAILED: 'Falha no envio',
    DELETED: 'Removido',
}

export const attachmentStatusLabelSafe = (s: string) =>
    attachmentStatusLabel[s] ?? 'Documento'

// ---------------------------------------------------------------------------
// Sistemas externos (nomes comerciais)
// ---------------------------------------------------------------------------

export const externalSystemLabel = (s?: string | null) => {
    const map: Record<string, string> = {
        BRINDE_ME: 'Brinde.me',
        TIFLUX: 'Tiflux',
        PIPEDRIVE: 'Pipedrive',
        'CONTA_AZUL': 'Conta Azul',
    }
    return s ? (map[s.toUpperCase()] ?? s) : '—'
}

// ---------------------------------------------------------------------------
// Auditoria — inventário completo dos enums reais do backend
// ---------------------------------------------------------------------------

/**
 * Eventos técnicos que NÃO geram ruído para o usuário de negócio (§27).
 * Continuam auditáveis no backend, mas fora da timeline visual.
 */
export const HIDDEN_AUDIT_ACTIONS = ['ATTACHMENT_COMPLETED']

export const auditActionLabel: Record<string, string> = {
    PROCESS_CREATED: 'Processo criado',
    PROCESS_UPDATED: 'Processo atualizado',
    STATUS_CHANGED: 'Status alterado',
    STAGE_CHANGED: 'Estágio alterado',
    ASSIGNEE_CHANGED: 'Responsável alterado',
    SOURCE_SYNCED: 'Origem sincronizada',
    PROCESS_BATCH_CREATED: 'Processos criados em lote',
    SOURCE_ITEM_MISSING: 'Item do orçamento não encontrado na origem',

    SUPPLIER_QUOTE_CREATED: 'Cotação criada',
    SUPPLIER_QUOTE_UPDATED: 'Cotação atualizada',
    SUPPLIER_QUOTE_STATUS_CHANGED: 'Status da cotação alterado',
    SUPPLIER_QUOTE_SELECTED: 'Cotação selecionada como vencedora',
    SUPPLIER_QUOTE_SELECTION_CHANGED: 'Vencedora substituída',
    SUPPLIER_QUOTE_DELETED: 'Cotação removida',

    PURCHASE_CREATED: 'Compra gerada',
    PURCHASE_UPDATED: 'Compra atualizada',
    PURCHASE_STATUS_CHANGED: 'Status da compra alterado',
    PURCHASE_CANCELED: 'Compra cancelada',
    PURCHASE_RECEIVED: 'Compra recebida',
    PURCHASE_CHECKED: 'Compra conferida',

    PAYMENT_CREATED: 'Pagamento criado',
    PAYMENT_UPDATED: 'Pagamento atualizado',
    PAYMENT_PAID: 'Pagamento realizado',
    PAYMENT_CANCELED: 'Pagamento cancelado',

    ATTACHMENT_CREATED: 'Documento adicionado',
    ATTACHMENT_COMPLETED: 'Envio concluído', // não exibido (HIDDEN)
    ATTACHMENT_FAILED: 'Falha ao enviar documento',
    ATTACHMENT_DELETED: 'Documento removido',

    PRODUCTION_LINKED: 'Ticket de produção vinculado',
    PRODUCTION_UPDATED: 'Ticket de produção atualizado',
    PRODUCTION_LINK_CONFIRMED: 'Vínculo de produção confirmado',
    PRODUCTION_LINK_UNLINKED: 'Vínculo de produção removido',
}

/** §30 — nunca fallback para o enum cru; fallback humano seguro. */
export const auditActionLabelSafe = (action: string) =>
    auditActionLabel[action] ?? 'Evento atualizado'

// ---------------------------------------------------------------------------
// Outros enums da UI (payment "Sinal" → Adiantamento etc.)
// ---------------------------------------------------------------------------

export const fileSizeLabel = (bytes: number) => {
    if (bytes >= 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', {
            maximumFractionDigits: 1,
        })} MB`
    return `${Math.max(1, Math.round(bytes / 1024))} KB`
}
